// react
import { useCallback, useEffect, useState } from 'react';

// types
import { VoucherPayload, Voucher } from '../@types';

// services
import {
	createVoucher,
	fetchVoucher,
	fetchVouchers,
	updateVoucher,
	ApiResponse,
	QueryParams,
} from '../services/api';

// Types

export interface UseVouchersParams {
	contractId?: number;
	isEnabled?: boolean;
	projectId?: number;
	query?: QueryParams;
}

export interface UseVouchersReturn {
	create: (payload: VoucherPayload) => Promise<ApiResponse<Voucher>>;
	data: Voucher[];
	error: string | null;
	loading: boolean;
	pagination: ApiResponse<Voucher[]>['pagination'];
	refetch: () => Promise<void>;
	update: (id: number, payload: Partial<VoucherPayload>) => Promise<ApiResponse<Voucher>>;
}

// Hook

export const useVouchers = ({
	contractId,
	isEnabled = true,
	projectId,
	query = {},
}: UseVouchersParams = {}): UseVouchersReturn => {
	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [data, setData] = useState<Voucher[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState<ApiResponse<Voucher[]>['pagination']>(undefined);

	// fetch data
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const queryKey = JSON.stringify(query);

	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const res = await fetchVouchers(
				{ accessToken, idToken },
				{ ...query, contract: contractId, project: projectId }
			);

			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || []);
				setPagination(res.pagination);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch vouchers');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, contractId, projectId, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && projectId) {
			fetchData();
		}
	}, [isEnabled, fetchData, projectId]);

	// create voucher
	const create = useCallback(
		async (payload: VoucherPayload) => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			const res = await createVoucher({ accessToken, idToken }, payload);

			if (!res.error) {
				await fetchData();
			}

			return res;
		},
		[accessToken, idToken, fetchData]
	);

	// update voucher
	const update = useCallback(
		async (id: number, payload: Partial<VoucherPayload>) => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			const res = await updateVoucher({ accessToken, idToken }, id, payload);

			if (!res.error) {
				await fetchData();
			}

			return res;
		},
		[accessToken, idToken, fetchData]
	);

	// delete voucher — HTTP DELETE banished; no UI caller

	return {
		create,
		data,
		error,
		loading,
		pagination,
		refetch: fetchData,
		update,
	};
};

// Single Voucher Hook

export interface UseVoucherParams {
	id: number;
	isEnabled?: boolean;
	query?: QueryParams;
}

export interface UseVoucherReturn {
	data: Voucher | null;
	error: string | null;
	loading: boolean;
	refetch: () => Promise<void>;
	update: (payload: Partial<VoucherPayload>) => Promise<ApiResponse<Voucher>>;
}

export const useVoucher = ({
	id,
	isEnabled = true,
	query = {},
}: UseVoucherParams): UseVoucherReturn => {
	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [data, setData] = useState<Voucher | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	// fetch data
	const queryKey = JSON.stringify(query);

	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken || !id) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const res = await fetchVoucher({ accessToken, idToken }, id, query);

			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || null);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch voucher');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, id, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && id) {
			fetchData();
		}
	}, [isEnabled, fetchData, id]);

	// update voucher
	const update = useCallback(
		async (payload: Partial<VoucherPayload>) => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			const res = await updateVoucher({ accessToken, idToken }, id, payload);

			if (!res.error) {
				await fetchData();
			}

			return res;
		},
		[accessToken, idToken, id, fetchData]
	);

	return {
		data,
		error,
		loading,
		refetch: fetchData,
		update,
	};
};
